import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit2, Trash2, Users, MapPin, Phone, Mail, X, Save, Upload } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { uploadEscolaLogo, updateEscolaLogo, deleteEscolaLogo } from '../utils/imageUpload';

const Escolas = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [escolas, setEscolas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEscola, setEditingEscola] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    cnpj: '',
    endereco: '',
    cep: '',
    logradouro: '',
    numero: '',
    bairro: '',
    cidade: '',
    estado: '',
    telefone: '',
    email: '',
    site: '',
    status: 'ativo',
    user_chat: false,
    logotipo: '',
    logoFile: null,
    logoPath: ''
  });

  // Carregar escolas do Supabase
  useEffect(() => {
    fetchEscolas();
  }, []);

  const fetchEscolas = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('escolas')
        .select('*')
        .order('nome');

      if (error) {
        console.error('Erro ao carregar escolas:', error);
        return;
      }

      setEscolas(data || []);
    } catch (error) {
      console.error('Erro ao carregar escolas:', error);
    } finally {
      setLoading(false);
    }
  };

  // Criar nova escola
  const createEscola = async (escolaData) => {
    try {
      let logoUrl = null;
      let logoPath = null;

      // Se há um arquivo de logotipo, fazer upload
      if (escolaData.logoFile) {
        const uploadResult = await uploadEscolaLogo(escolaData.logoFile, 'temp-' + Date.now());
        if (uploadResult.success) {
          logoUrl = uploadResult.url;
          logoPath = uploadResult.path;
        } else {
          alert('Erro no upload do logotipo: ' + uploadResult.error);
          return false;
        }
      }

      // Preparar dados para inserção (remover campos de arquivo)
      const { logoFile, ...dataToInsert } = escolaData;
      if (logoUrl) {
        dataToInsert.logotipo = logoUrl;
        dataToInsert.logo_path = logoPath;
      }

      const { data, error } = await supabase
        .from('escolas')
        .insert([dataToInsert])
        .select();

      if (error) {
        console.error('Erro ao criar escola:', error);
        alert('Erro ao criar escola: ' + error.message);
        // Se houve erro, deletar o arquivo que foi feito upload
        if (logoPath) {
          await deleteEscolaLogo(logoPath);
        }
        return false;
      }

      // Atualizar o caminho do arquivo com o ID real da escola
      if (logoPath && data[0]) {
        // Aqui você poderia renomear o arquivo no storage se necessário
        // const newLogoPath = logoPath.replace('temp-' + Date.now(), data[0].id);
      }

      setEscolas([...escolas, data[0]]);
      return true;
    } catch (error) {
      console.error('Erro ao criar escola:', error);
      alert('Erro ao criar escola');
      return false;
    }
  };

  // Atualizar escola existente
  const updateEscola = async (id, escolaData) => {
    try {
      let logoUrl = escolaData.logotipo;
      let logoPath = escolaData.logoPath;
      const escolaAtual = escolas.find(e => e.id === id);

      // Se há um novo arquivo de logotipo, fazer upload
      if (escolaData.logoFile) {
        const uploadResult = await updateEscolaLogo(
          escolaData.logoFile, 
          id, 
          escolaAtual?.logo_path
        );
        if (uploadResult.success) {
          logoUrl = uploadResult.url;
          logoPath = uploadResult.path;
        } else {
          alert('Erro no upload do logotipo: ' + uploadResult.error);
          return false;
        }
      }

      // Preparar dados para atualização (remover campos de arquivo)
      const { logoFile, ...dataToUpdate } = escolaData;
      if (logoUrl) {
        dataToUpdate.logotipo = logoUrl;
        dataToUpdate.logo_path = logoPath;
      }

      const { data, error } = await supabase
        .from('escolas')
        .update(dataToUpdate)
        .eq('id', id)
        .select();

      if (error) {
        console.error('Erro ao atualizar escola:', error);
        alert('Erro ao atualizar escola: ' + error.message);
        return false;
      }

      setEscolas(escolas.map(escola => 
        escola.id === id ? data[0] : escola
      ));
      return true;
    } catch (error) {
      console.error('Erro ao atualizar escola:', error);
      alert('Erro ao atualizar escola');
      return false;
    }
  };

  // Excluir escola
  const deleteEscola = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta escola?')) {
      return;
    }

    try {
      const escolaAtual = escolas.find(e => e.id === id);
      
      const { error } = await supabase
        .from('escolas')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Erro ao excluir escola:', error);
        alert('Erro ao excluir escola: ' + error.message);
        return;
      }

      // Se a escola tinha logotipo, deletar o arquivo
      if (escolaAtual?.logo_path) {
        await deleteEscolaLogo(escolaAtual.logo_path);
      }

      setEscolas(escolas.filter(escola => escola.id !== id));
    } catch (error) {
      console.error('Erro ao excluir escola:', error);
      alert('Erro ao excluir escola');
    }
  };

  // Abrir modal para nova escola
  const openCreateModal = () => {
    // Função para resetar o formulário
    const resetForm = () => {
      setFormData({
        nome: '',
        cnpj: '',
        endereco: '',
        cep: '',
        logradouro: '',
        numero: '',
        bairro: '',
        cidade: '',
        estado: '',
        telefone: '',
        email: '',
        site: '',
        status: 'ativo',
        user_chat: false,
        logotipo: '',
        logoFile: null,
        logoPath: ''
      });
      setEditingEscola(null);
    };

    resetForm();
    setShowModal(true);
  };

  // Abrir modal para editar escola
  const openEditModal = (escola) => {
    setEditingEscola(escola);
    setFormData({
      nome: escola.nome || '',
      cnpj: escola.cnpj || '',
      endereco: escola.endereco || '',
      cep: escola.cep || '',
      logradouro: escola.logradouro || '',
      numero: escola.numero || '',
      bairro: escola.bairro || '',
      cidade: escola.cidade || '',
      estado: escola.estado || '',
      telefone: escola.telefone || '',
      email: escola.email || '',
      site: escola.site || '',
      status: escola.status || 'ativo',
      user_chat: escola.user_chat || false,
      logotipo: escola.logotipo || '',
      logoFile: null,
      logoPath: escola.logo_path || ''
    });
    setShowModal(true);
  };

  // Fechar modal
  const closeModal = () => {
    setShowModal(false);
    setEditingEscola(null);
  };

  // Salvar escola (criar ou atualizar)
  const handleSave = async (e) => {
    e.preventDefault();
    
    // Validações básicas
    if (!formData.nome) {
      alert('Por favor, preencha o campo obrigatório: Nome');
      return;
    }

    const success = editingEscola 
      ? await updateEscola(editingEscola.id, formData)
      : await createEscola(formData);

    if (success) {
      closeModal();
    }
  };

  // Atualizar campo do formulário
  const updateFormField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };



  const filteredEscolas = escolas.filter(escola => {
    const matchesSearch = escola.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         escola.cidade?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         escola.estado?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'todos' || escola.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Calcular estatísticas
  const escolasAtivas = escolas.filter(escola => escola.status === 'ativo').length;
  const totalCidades = [...new Set(escolas.map(escola => escola.cidade).filter(Boolean))].length;
  const totalEstados = [...new Set(escolas.map(escola => escola.estado).filter(Boolean))].length;

  if (loading) {
    return (
      <div className="escolas">
        <div className="page-header">
          <h1 className="page-title">Escolas</h1>
          <p className="page-subtitle">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="escolas">
      <div className="page-header">
        <h1 className="page-title">Escolas</h1>
        <p className="page-subtitle">Gerenciamento de instituições de ensino</p>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-header">
            <div className="stat-icon">
              <Users size={24} />
            </div>
          </div>
          <div className="stat-value">{escolas.length}</div>
          <div className="stat-label">Total de Escolas</div>
        </div>

        <div className="stat-card success">
          <div className="stat-header">
            <div className="stat-icon success">
              <Users size={24} />
            </div>
          </div>
          <div className="stat-value">{escolasAtivas}</div>
          <div className="stat-label">Escolas Ativas</div>
        </div>

        <div className="stat-card warning">
          <div className="stat-header">
            <div className="stat-icon warning">
              <MapPin size={24} />
            </div>
          </div>
          <div className="stat-value">{totalCidades}</div>
          <div className="stat-label">Cidades</div>
        </div>

        <div className="stat-card danger">
          <div className="stat-header">
            <div className="stat-icon danger">
              <MapPin size={24} />
            </div>
          </div>
          <div className="stat-value">{totalEstados}</div>
          <div className="stat-label">Estados</div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="content-card">
        <div className="filters-section">
          <div className="search-container">
            <Search size={20} className="search-icon" />
            <input
              type="text"
              placeholder="Buscar escolas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="filter-buttons">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="todos">Todos os Status</option>
              <option value="ativo">Ativo</option>
              <option value="inativo">Inativo</option>
            </select>
            
            <button className="btn btn-primary" onClick={openCreateModal}>
              <Plus size={16} />
              Nova Escola
            </button>
          </div>
        </div>

        {/* Schools Table */}
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Escola</th>
                <th>Cidade/Estado</th>
                <th>Contato</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredEscolas.map(escola => (
                <tr key={escola.id}>
                  <td>
                    <div className="school-info">
                      <div className="school-name">{escola.nome}</div>
                      <div className="school-address">
                        <MapPin size={14} />
                        {escola.endereco || `${escola.logradouro || ''} ${escola.numero || ''}, ${escola.bairro || ''}`}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="location-info">
                      <div>{escola.cidade}</div>
                      <div className="state-badge">{escola.estado}</div>
                    </div>
                  </td>
                  <td>
                    <div className="contact-info">
                      <div className="contact-item">
                        <Phone size={14} />
                        {escola.telefone || 'N/A'}
                      </div>
                      <div className="contact-item">
                        <Mail size={14} />
                        {escola.email || 'N/A'}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${escola.status}`}>
                      {escola.status === 'ativo' ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-icon" title="Editar" onClick={() => openEditModal(escola)}>
                        <Edit2 size={16} />
                      </button>
                      <button className="btn-icon danger" title="Excluir" onClick={() => deleteEscola(escola.id)}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredEscolas.length === 0 && (
          <div className="empty-state">
            <p>Nenhuma escola encontrada com os filtros aplicados.</p>
          </div>
        )}
      </div>

      {/* Modal de Cadastro/Edição */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingEscola ? 'Editar Escola' : 'Nova Escola'}</h2>
              <button className="modal-close" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSave} className="modal-form">
              <div className="form-grid">
                <div className="form-group">
                  <label>Nome *</label>
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={(e) => updateFormField('nome', e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>CNPJ</label>
                  <input
                    type="text"
                    value={formData.cnpj}
                    onChange={(e) => updateFormField('cnpj', e.target.value)}
                  />
                </div>

                <div className="form-group full-width">
                  <label>Endereço</label>
                  <input
                    type="text"
                    value={formData.endereco}
                    onChange={(e) => updateFormField('endereco', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>CEP</label>
                  <input
                    type="text"
                    value={formData.cep}
                    onChange={(e) => updateFormField('cep', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Logradouro</label>
                  <input
                    type="text"
                    value={formData.logradouro}
                    onChange={(e) => updateFormField('logradouro', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Número</label>
                  <input
                    type="text"
                    value={formData.numero}
                    onChange={(e) => updateFormField('numero', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Bairro</label>
                  <input
                    type="text"
                    value={formData.bairro}
                    onChange={(e) => updateFormField('bairro', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Cidade</label>
                  <input
                    type="text"
                    value={formData.cidade}
                    onChange={(e) => updateFormField('cidade', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Estado</label>
                  <select
                    value={formData.estado}
                    onChange={(e) => updateFormField('estado', e.target.value)}
                  >
                    <option value="">Selecione uma opção</option>
                    <option value="AC">Acre</option>
                    <option value="AL">Alagoas</option>
                    <option value="AP">Amapá</option>
                    <option value="AM">Amazonas</option>
                    <option value="BA">Bahia</option>
                    <option value="CE">Ceará</option>
                    <option value="DF">Distrito Federal</option>
                    <option value="ES">Espírito Santo</option>
                    <option value="GO">Goiás</option>
                    <option value="MA">Maranhão</option>
                    <option value="MT">Mato Grosso</option>
                    <option value="MS">Mato Grosso do Sul</option>
                    <option value="MG">Minas Gerais</option>
                    <option value="PA">Pará</option>
                    <option value="PB">Paraíba</option>
                    <option value="PR">Paraná</option>
                    <option value="PE">Pernambuco</option>
                    <option value="PI">Piauí</option>
                    <option value="RJ">Rio de Janeiro</option>
                    <option value="RN">Rio Grande do Norte</option>
                    <option value="RS">Rio Grande do Sul</option>
                    <option value="RO">Rondônia</option>
                    <option value="RR">Roraima</option>
                    <option value="SC">Santa Catarina</option>
                    <option value="SP">São Paulo</option>
                    <option value="SE">Sergipe</option>
                    <option value="TO">Tocantins</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Telefone</label>
                  <input
                    type="text"
                    value={formData.telefone}
                    onChange={(e) => updateFormField('telefone', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>E-mail</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateFormField('email', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Site</label>
                  <input
                    type="url"
                    value={formData.site}
                    onChange={(e) => updateFormField('site', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="status"
                        value="ativo"
                        checked={formData.status === 'ativo'}
                        onChange={(e) => updateFormField('status', e.target.value)}
                      />
                      Ativo
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="status"
                        value="inativo"
                        checked={formData.status === 'inativo'}
                        onChange={(e) => updateFormField('status', e.target.value)}
                      />
                      Inativo
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label>Pode acessar conversas?</label>
                  <div className="toggle-container">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={formData.user_chat}
                        onChange={(e) => updateFormField('user_chat', e.target.checked)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                    <span className="toggle-label">
                      {formData.user_chat ? 'Habilitado' : 'Desabilitado'}
                    </span>
                  </div>
                </div>

                <div className="form-group full-width">
                  <label>Logotipo</label>
                  <div className="file-upload-area">
                    {formData.logotipo && (
                      <div className="current-logo">
                        <img 
                          src={formData.logotipo} 
                          alt="Logotipo atual" 
                          className="logo-preview"
                        />
                        <p className="logo-info">Logotipo atual</p>
                      </div>
                    )}
                    <div className="upload-section">
                      <Upload size={24} className="upload-icon" />
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            // Validar tamanho (5MB)
                            if (file.size > 5 * 1024 * 1024) {
                              alert('Arquivo muito grande. Máximo 5MB.');
                              e.target.value = '';
                              return;
                            }
                            
                            // Armazenar o arquivo para upload posterior
                            updateFormField('logoFile', file);
                            
                            // Mostrar preview
                            const reader = new FileReader();
                            reader.onload = (event) => {
                              updateFormField('logotipo', event.target.result);
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        className="file-input"
                      />
                      <p>Arraste e solte os arquivos ou Clique aqui</p>
                      <small>Formatos aceitos: JPEG, PNG, GIF, WebP (máx. 5MB)</small>
                    </div>
                  </div>
                </div>


              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  <Save size={16} />
                  {editingEscola ? 'Atualizar' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          border-radius: 12px;
          width: 90%;
          max-width: 800px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 24px;
          border-bottom: 1px solid #e5e7eb;
        }

        .modal-header h2 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
          color: #1e293b;
        }

        .modal-close {
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          color: #64748b;
        }

        .modal-close:hover {
          background: #f1f5f9;
          color: #1e293b;
        }

        .modal-form {
          padding: 24px;
        }

        .form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-bottom: 24px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .form-group.full-width {
          grid-column: 1 / -1;
        }

        .form-group label {
          font-weight: 500;
          color: #374151;
          font-size: 14px;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
          transition: border-color 0.2s ease;
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .modal-footer {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          padding-top: 16px;
          border-top: 1px solid #e5e7eb;
        }

        .btn {
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s ease;
        }

        .btn-primary {
          background: #3b82f6;
          color: white;
          border: none;
        }

        .btn-primary:hover {
          background: #2563eb;
        }

        .btn-secondary {
          background: #f8fafc;
          color: #64748b;
          border: 1px solid #d1d5db;
        }

        .btn-secondary:hover {
          background: #f1f5f9;
          color: #475569;
        }

        .filters-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          gap: 16px;
        }

        .search-container {
          position: relative;
          flex: 1;
          max-width: 400px;
        }

        .search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
        }

        .search-input {
          width: 100%;
          padding: 12px 12px 12px 44px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          transition: border-color 0.2s ease;
        }

        .search-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .filter-buttons {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .filter-select {
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          background: white;
        }

        .table-container {
          overflow-x: auto;
        }

        .school-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .school-name {
          font-weight: 600;
          color: #1e293b;
        }

        .location-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .state-badge {
          display: inline-block;
          padding: 2px 8px;
          background: #f1f5f9;
          color: #475569;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
        }

        .radio-group {
          display: flex;
          gap: 16px;
          margin-top: 8px;
        }

        .radio-label {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          font-size: 14px;
        }

        .radio-label input[type="radio"] {
          margin: 0;
        }

        .file-upload-area {
          border: 2px dashed #d1d5db;
          border-radius: 8px;
          padding: 24px;
          text-align: center;
          background: #f9fafb;
          transition: border-color 0.2s ease;
        }

        .file-upload-area:hover {
          border-color: #3b82f6;
          background: #f0f9ff;
        }

        .current-logo {
          margin-bottom: 16px;
          padding: 12px;
          background: white;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
        }

        .logo-preview {
          max-width: 120px;
          max-height: 80px;
          object-fit: contain;
          border-radius: 4px;
        }

        .logo-info {
          margin: 8px 0 0 0;
          font-size: 12px;
          color: #6b7280;
        }

        .upload-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .upload-icon {
          color: #9ca3af;
        }

        .file-input {
          margin: 0;
        }

        .upload-section p {
          margin: 0;
          color: #6b7280;
          font-size: 14px;
        }

        .upload-section small {
          color: #9ca3af;
          font-size: 12px;
        }

        .full-width {
          grid-column: 1 / -1;
        }

        .school-address {
          display: flex;
          align-items: center;
          gap: 4px;
          color: #64748b;
          font-size: 12px;
        }

        .school-code {
          background: #f1f5f9;
          padding: 4px 8px;
          border-radius: 4px;
          font-family: monospace;
          font-size: 12px;
          font-weight: 600;
          color: #475569;
        }

        .contact-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .contact-item {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .toggle-container {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-top: 8px;
        }

        .toggle-switch {
          position: relative;
          display: inline-block;
          width: 50px;
          height: 24px;
        }

        .toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
          transition: 0.3s;
          border-radius: 24px;
        }

        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 18px;
          width: 18px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: 0.3s;
          border-radius: 50%;
        }

        input:checked + .toggle-slider {
          background-color: #3b82f6;
        }

        input:checked + .toggle-slider:before {
          transform: translateX(26px);
        }

        .toggle-label {
          font-size: 14px;
          color: #374151;
          font-weight: 500;
        }
          color: #64748b;
          font-size: 12px;
        }

        .number-badge {
          background: #eff6ff;
          color: #3b82f6;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
        }

        .action-buttons {
          display: flex;
          gap: 8px;
        }

        .btn-icon {
          padding: 8px;
          border: none;
          border-radius: 6px;
          background: #f8fafc;
          color: #64748b;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .btn-icon:hover {
          background: #e2e8f0;
          color: #3b82f6;
        }

        .btn-icon.danger:hover {
          background: #fef2f2;
          color: #ef4444;
        }

        .empty-state {
          text-align: center;
          padding: 48px 24px;
          color: #64748b;
        }

        @media (max-width: 768px) {
          .form-grid {
            grid-template-columns: 1fr;
          }

          .filters-section {
            flex-direction: column;
            align-items: stretch;
          }

          .search-container {
            max-width: none;
          }
        }
      `}</style>
    </div>
  );
};

export default Escolas;
